/**
* RTV Comment Widget
*
* Usage:
* 1. Include Bootstrap 4 CSS & JS in your HTML page.
* 2. Include this script file in your HTML page (ideally at the end of the body).
* 3. Ensure your HTML has a container element: <div id="rtv-comment-widget-container"></div>
* 4. Add trigger buttons with class="rtv-comment-trigger-button" and data-newsid="..."
*    attribute where you want them to appear. Example:
*    <button class="btn btn-sm btn-outline-secondary rtv-comment-trigger-button" data-newsid="12345" type="button">Comments</button>
*/
class RTVCommentWidget {
  
  // --- Initialization ---
  constructor() {
    
    var self = this;

    const isDev = typeof SKIT_APP_HELPER == 'object' && SKIT_APP_HELPER.isDev();
    
    // --- Configuration ---
    this.clientId = '8c5205a95060a482f0fc96b9162d9e3f';
    this.API_BASE = 'https://api.' + (isDev ? 'dev.' : '') + 'rtvslo.si';
    this.TRIGGER_BUTTON_SELECTOR = '.rtv-comment-trigger-button';
    this.STYLE_ID = 'rtv-comment-widget-styles';
    
    // --- State Variables ---
    this.currentNewsId = null;
    this.drawerElement = null;
    this.commentListElement = null;
    this.commentFormElement = null;
    this.commentInputElement = null;
    this.commentStatusElement = null;
    this.drawerTitleElement = null;
    this.isClosingProgrammatically = false; // Flag to prevent immediate reopening on outside click

    // Comments fetching
    this.commentPage = 0;
    this.commentPerPage = 10;
    this.commentCount = null;
    this.isModerator = false;

    // Append container
    this.container = document.createElement('div');
    this.container.id = 'rtv-comment-widget-container';
    document.body.appendChild(this.container);
    
    // --- CSS Styles ---
    this.cssStyles = `
    #rtv-comment-drawer {
      position: fixed;
      bottom: 0;
      left: 20px;
      width: 500px;
      max-height: 85vh; /* Limit height on mobile */
      background-color: #fff;
      box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.15);
      z-index: 1040; /* Above most elements, consider Bootstrap modal z-index (1050) */
      transform: translateY(100%); /* Start hidden below */
      transition: transform 0.3s ease-in-out;
      display: flex;
      flex-direction: column;
      border-top-left-radius: 15px; /* Optional: rounded top corners */
      border-top-right-radius: 15px;
      overflow: hidden;
    }
    #rtv-comment-drawer.open {
      transform: translateY(0); /* Slide in */
    }

    @media (max-width: 575.98px) {
      #rtv-comment-drawer {
        bottom: 0;
        left: 0;
        width: 100%;
      }
    }

    .comment-drawer-header {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #dee2e6;
      background-color: #f8f9fa;
      display: flex;
      justify-content: space-between;
      align-items: center;
      flex-shrink: 0; /* Prevent header from shrinking */
      z-index: 2; /* Above body */
    }
    .comment-drawer-body {
      padding: 1rem;
      overflow-y: auto; /* Enable scrolling for comments */
      flex-grow: 1; /* Take remaining height */
      -webkit-overflow-scrolling: touch; /* Smooth scrolling on iOS */
    }
    .comment-drawer-footer {
      padding: 0.75rem 1rem;
      border-top: 1px solid #dee2e6;
      background-color: #f8f9fa; /* Light background for input area */
      flex-shrink: 0; /* Prevent footer from shrinking */
      z-index: 2; /* Above body */
    }
    #rtv-comment-list .list-group-item {
      border: none; /* Cleaner look */
      border-bottom: 1px solid #eee; /* Separator */
      padding: 0.75rem 0; /* Adjust padding */
      word-wrap: break-word;
    }
    #rtv-comment-list .list-group-item:last-child {
      border-bottom: none;
    }
    .comment-author-img {
      height: 1.5em;
      width: 1.5em;
      border-radius: 1em;
      margin-right: 5px;
    }
    .comment-author {
      font-weight: bold;
      margin-right: 8px;
      color: #333;
    }
    .comment-date {
      font-size: 0.8em;
      color: #6c757d;
    }
    
    .comment-vote {
      float: right;
    }

    .comment-text {
      margin-top: 5px;
      white-space: pre-wrap; /* Preserve line breaks */
      word-wrap: break-word; /* Prevent long words from overflowing */
      color: #212529;
      font-size: 0.95rem; /* Slightly smaller text */
    }
    .comments-loading,
    .comments-error {
      text-align: center;
      padding: 2rem 1rem;
      color: #6c757d;
    }
    .comments-error.alert { /* Style for Bootstrap alert */
      margin-bottom: 0;
    }
    /* Ensure drawer is above potential Bootstrap modal backdrops if any */
    .modal-backdrop {
      z-index: 1035;
    }
    /* Style for the close button */
    #rtv-comment-close-btn {
      padding: 0.5rem; /* Easier to tap */
      line-height: 1;
      font-size: 1.5rem;
      opacity: 0.7;
    }
    #rtv-comment-close-btn:hover {
      opacity: 1;
    }`;
    
    this.injectCommentStyles(); // Add CSS to the page
    this.createDrawerHTML(); // Create the drawer structure
    
    // Initial setup: ensure drawer is hidden
    this.drawerElement.setAttribute('aria-hidden', 'true');

    // Dropdown
    window.openSimpleDropdown = function(clickedBtn) {
      var btnToggle = clickedBtn;
      var dropdownAll = btnToggle.parentElement;
      var dropdownContainer = dropdownAll.querySelector('.dropdown-container');

      dropdownContainer.classList.toggle("show");

      btnToggle.onclick = function(evt) {
        evt.preventDefault();
        dropdownContainer.classList.toggle("show");
      };

      // Close on outside click
      document.addEventListener('click', function(evt) {
        if (!dropdownAll.contains(evt.target) && dropdownContainer) dropdownContainer.classList.remove("show");
      });
    }

    window.deleteComment = async function(markedCommentId) {

      let text = "Želite izbrisati komentar?";
      if (confirm(text) == true) {
        try {
          let url = `${self.API_BASE}/moderation/updateComment?client_id=${self.clientId}`;

          // Build the POST body
          const formData = new URLSearchParams();
          formData.append('commentID', markedCommentId);
          formData.append('kind', 2); // 2 = delete

          let response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded'
            },
            body: formData.toString(),
            credentials: 'include'
          });

          response = await response.json();

          if (response && response.response) {
            console.log(response);

            // Remove comment from DOM
            let deletedComment = document.querySelector('.list-group-item[data-comment-id="' + markedCommentId + '"]');
            deletedComment.remove();

            alert('Komentar izbrisan!');
            //location.reload();
          } else {
            if (response.error) {
              alert('Zaznana napaka: ' + response.error.message + ', komentar ni zbrisan')
            } else {
              alert('Napaka pri brisanju komentarja!')
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
    }

    window.rateComment = async function(element) {

      // If user logged out
      if ( !self.cookieExist('APISESSION_USER_ID') ) {
        commentWidget.closeDrawer();
        new BSN.Modal(document.getElementById('loggedOutModal')).show();
        return;
      }

      var comment_id = Number(element.dataset.commentId);
      var voted = (element.dataset.voted === 'true');

      const postUrl = `${self.API_BASE}/comments/comment/${comment_id}/rating?client_id=${self.clientId}&rating=up`;
  
      try {
        const response = await fetch(postUrl, {
          method: 'POST',
          credentials: 'include', // Send cookies etc.
        });
        
        if (response.error) {
          let errorData = null;
          let errorMessage = `Napaka pri ocenjevanju komentarja. Status: ${response.status}`;
          try {
            // Try to parse error response from API
            errorData = await response.json();
            errorMessage = errorData?.message || errorData?.error || errorMessage;
          } catch (e) { /* Ignore if body is not JSON */ }
          throw new Error(errorMessage);
        }
        
        // Success!
        let addRate = (voted ? -1 : 1)
        let sumRate = Number(element.textContent) + addRate;
        element.textContent = sumRate;

        if (voted) {
          element.classList.remove('voted')
          element.dataset.voted = 'false';
        } else {
          element.classList.add('voted');
          element.dataset.voted = 'true';
        }
        
        
      } catch (error) {
        console.error(`Napaka pri ocenjevanju komentarja:`, error);
      }
      
    }

    // Report comment
    window.submitReport = async function(button) {
      const form = button.closest('.report-form');
      if (!form) return;

      const commentId = form.dataset.commentId;
      const reason = form.querySelector('.report-reason').value;
      const message = form.querySelector('.report-text').value;

      if (!reason) {
        alert("Prosim izberi razlog prijave.");
        return;
      }

      const url = `${self.API_BASE}/news/reportComment/${commentId}?client_id=${self.clientId}`;
      const data = {
        log_reason: reason,
        log_message: message
      };

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        });

        if (!response.ok) throw new Error("Napaka pri pošiljanju.");

        const result = await response.json();
        
        alert("Prijava uspešno poslana.");
        form.reset();
      } catch (error) {
        console.error("Napaka:", error);
        alert("Prišlo je do napake pri pošiljanju.");
      }
    }

    // Check if comment moderators
    let  userGroups = sessionStorage.getItem('userGroups');
    if (userGroups) {
        const userGroupsList = userGroups.split(",");
        this.isModerator = userGroupsList.some(num => Number(num) === Number(24));
    }

  }

  openComments = (event) => {

    event.preventDefault(); // Good practice for buttons
    const newsId = event.currentTarget.dataset.newsid;
    
    if (newsId) {
      // If drawer is already open for a DIFFERENT id, close it first then open
      if (this.drawerElement.classList.contains('open') && this.currentNewsId !== newsId) {
        this.closeDrawer();
        // Short delay to allow close animation before reopen
        setTimeout(() => this.openDrawer(newsId), 50);
      } else if (!this.drawerElement.classList.contains('open')) {
        this.openDrawer(newsId); // Open if closed
      }
      // If already open for the SAME id, do nothing or maybe refocus input?
      // else if (this.drawerElement.classList.contains('open') && this.currentNewsId === newsId) {
      //     commentInputElement.focus();
      // }
    } else {
      console.warn("Comment trigger button clicked without a 'data-newsid' attribute.", event.currentTarget);
    }

  }
  
  // --- Function to Inject CSS into <head> ---
  injectCommentStyles = () => {
    if (document.getElementById(this.STYLE_ID)) return; // Prevent double injection
    
    const styleElement = document.createElement('style');
    styleElement.id = this.STYLE_ID;
    styleElement.textContent = this.cssStyles;
    document.head.appendChild(styleElement);
  }

  // Cookie helper
  getCookie(name) {
    var value = "; " + document.cookie;
    var parts = value.split("; " + name + "=");
    if (parts.length == 2) return parts.pop().split(";").shift();
  }

  setCookie(name, value, days) {
    var d = new Date;
    d.setTime(d.getTime() + 24 * 60 * 60 * 1000 * days);
    document.cookie = name + "=" + value + ";domain=.rtvslo.si;path=/;expires=" + d.toGMTString();
  }

  deleteCookie(name) {
    cookieHelper.setCookie(name, '', -1);
  }

  cookieExist(cookieName) {
    if (document.cookie.split(';').filter(function(item) {
        return item.includes(cookieName + '=')
      }).length) {
      //console.log('Cookie founded!');
      return true;
    }
    return false;
  }
  // END: Cookie helper
  
  // --- Function to Create Drawer HTML ---
  createDrawerHTML = () => {
    
    var self = this;
    
    this.drawerElement = document.createElement('div');
    this.drawerElement.id = 'rtv-comment-drawer';
    this.drawerElement.setAttribute('role', 'dialog');
    this.drawerElement.setAttribute('aria-modal', 'true');
    this.drawerElement.setAttribute('aria-labelledby', 'rtv-comment-drawer-title');

    let formHTML = ( this.cookieExist('APISESSION_USER_ID') ) ?
      `<form id="rtv-comment-form" novalidate>
          <div class="input-group">
            <input type="text" id="rtv-comment-input" class="form-control" placeholder="Komentiraj..." required aria-label="Dodaj komentar">
            <div class="input-group-append">
              <button class="btn btn-primary" type="submit">Pošlji</button>
            </div>
          </div>
          <div class="invalid-feedback" style="width: 100%;">Please enter a comment.</div> <!-- Feedback for validation -->
        </form>` :
      `<div class="input-group justify-content-center">
        <button onclick="commentWidget.closeDrawer(); new BSN.Modal(document.getElementById('loggedOutModal')).show();" class="btn btn-primary" href="javascript:void(0);">Prijavite se</button>
      </div>`
    
    this.drawerElement.innerHTML = `<div class="comment-drawer-header">
        <h5 class="mb-0" id="rtv-comment-drawer-title">Komentarji</h5><a href="javascript:document.getElementById('rtv-comment-info').classList.toggle('d-none');document.querySelector('#rtv-comment-drawer .comment-drawer-body').scroll(0,0);" style="margin-right: auto; margin-left: 10px; font-size: 18px; align-self: anchor-center; color: rgba(0, 0, 0, .5); height: 20px;"><span class="icon-info-outline" aria-hidden="true"></span><span class="sr-only">Več o komentiranju</span></a>
        <button type="button" class="close" aria-label="Close" id="rtv-comment-close-btn" title="Close Comments">
            <span aria-hidden="true">×</span>
        </button>
      </div>
      <div class="comment-drawer-body">
        <div id="rtv-comment-info" class="d-none alert alert-info" style="font-size: 0.8em;">
          <p>Komentarji ne odražajo stališča uredniške politike RTV Slovenija.</p>
          <ul>
            <li><a href="javascript:commentWidget.closeDrawer();SKIT_APP_RTVSLO.showNews('487653')" style="color: inherit; font-weight: 600;">POGOSTA VPRAŠANJA IN ODGOVORI</a></li>
            <li><a href="javascript:commentWidget.closeDrawer();SKIT_APP_RTVSLO.showNews('455920')" style="color: inherit; font-weight: 600;">STANDARDI IN PRAVILA KOMUNICIRANJA</a></li>
            <li><a href="https://www.rtvslo.si/mojrtv/komentarji/uporabniske-vsebine" target="_blank" style="color: inherit; font-weight: 600;">POŠLJI SPOROČILO ADMINISTRATORJEM</a></li>
          </ul>
        </div>
        <div id="rtv-comment-status" aria-live="polite"></div> <!-- For Loading/Error Messages -->
        <div id="rtv-comment-list" class="list-group list-group-flush">
          <!-- Comments will be loaded here -->
        </div>
        <div id="scroll-end-comments" style="height: 1px;"></div>
      </div>
      <div class="comment-drawer-footer">
        ${formHTML}
      </div>`;
    this.container.appendChild(this.drawerElement);
    
    // Get references to elements within the drawer
    this.commentListElement = this.drawerElement.querySelector('#rtv-comment-list');
    this.commentFormElement = this.drawerElement.querySelector('#rtv-comment-form');
    this.commentInputElement = this.drawerElement.querySelector('#rtv-comment-input');
    this.commentStatusElement = this.drawerElement.querySelector('#rtv-comment-status');
    this.drawerTitleElement = this.drawerElement.querySelector('#rtv-comment-drawer-title');
    const closeButton = this.drawerElement.querySelector('#rtv-comment-close-btn');
    
    // Add event listeners for elements WITHIN the drawer
    closeButton.addEventListener('click', this.closeDrawer);

    if (this.commentFormElement) {
      this.commentFormElement.addEventListener('submit', this.handleCommentSubmit);
    
      // Basic input validation feedback
      this.commentInputElement.addEventListener('input', () => {
        if (self.commentInputElement.value.trim()) {
          self.commentInputElement.classList.remove('is-invalid');
        }
      });
    }

    const scrollEndElement = this.drawerElement.querySelector('#scroll-end-comments');
    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          //console.log('Uporabnik je dosegel dno strani!');
          this.fetchComments();
          // Only one trigger
          //observer.unobserve(entry.target);
        }
      });
    });

    observer.observe(scrollEndElement);
    
    // Close drawer if clicking outside of it
    document.addEventListener('click', this.handleOutsideClick);
    // Also handle touch events for mobile closing
    document.addEventListener('touchstart', this.handleOutsideClick);
    
  }
  
  // --- Outside Click Handler ---
  handleOutsideClick = (event) => {
    if (!this.drawerElement || !this.drawerElement.classList.contains('open') || this.isClosingProgrammatically) {
      return;
    }
    // Check if the click/touch was outside the drawer AND not on any trigger button
    const isInsideDrawer = this.drawerElement.contains(event.target);
    const isTriggerButton = event.target.closest(this.TRIGGER_BUTTON_SELECTOR);
    
    if (!isInsideDrawer && !isTriggerButton) {
      this.closeDrawer();
    }
  }
  
  // --- Function to Open Drawer ---
  openDrawer = (newsId) => {
    if (!this.drawerElement || !newsId) return;
    
    var self = this;
    
    this.currentNewsId = newsId;
    if (this.drawerTitleElement) {
      // Example: Update title, keep it simple or add ID
      this.drawerTitleElement.textContent = `Komentarji`;
      // this.drawerTitleElement.textContent = `Comments (ID: ${newsId})`;
    }
    
    // Reset drawer state
    this.commentListElement.innerHTML = ''; // Clear previous comments
    this.drawerElement.querySelector('#rtv-comment-info').classList.add('d-none');
    this.commentCount = null;
    this.commentPage = 0;
    

    if (this.commentInputElement) {
      this.commentInputElement.value = ''; // Clear input field
      this.commentInputElement.classList.remove('is-invalid'); // Reset validation
      this.hideStatus(); // Clear loading/error messages

      // Focus the input field for better accessibility (after transition)
      setTimeout(() => {
        // self.commentInputElement.focus();
      }, 300); // Match CSS transition duration
    }
    
    this.drawerElement.classList.add('open');
    this.drawerElement.setAttribute('aria-hidden', 'false');
    
    this.fetchComments(); // Fetch comments for the new currentNewsId
    document.body.style.overflow = 'hidden'; // Disable body scroll
  }
  
  // --- Function to Close Drawer ---
  closeDrawer = () => {
    if (!this.drawerElement || !this.drawerElement.classList.contains('open')) return;

    var self = this;
    
    // Set flag to prevent immediate reopening from the same click/touch event
    this.isClosingProgrammatically = true;
    
    this.drawerElement.classList.remove('open');
    this.drawerElement.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = ''; // Re-enable body scroll
    
    // Reset state after closing
    this.currentNewsId = null;
    // Optional: clear content immediately or wait for transition end
    // this.commentListElement.innerHTML = '';
    // commentInputElement.value = '';
    this.hideStatus();
    if (this.drawerTitleElement) this.drawerTitleElement.textContent = 'Comments'; // Reset title
    
    // Reset flag after a short delay
    setTimeout(() => {
      self.isClosingProgrammatically = false;
    }, 50); // Small delay is usually enough
  }
  
  // --- Function to Display Status (Loading/Error) ---
  showStatus = (message, isError = false) => {
    if (!this.commentStatusElement) return;
    this.commentStatusElement.className = isError ? 'comments-error alert alert-danger p-3' : 'comments-loading p-3 text-center';
    this.commentStatusElement.textContent = message;
    this.commentStatusElement.style.display = 'block';
    if (this.commentListElement) {
      this.commentListElement.style.visibility = 'hidden'; // Hide list while showing status
    }
  }
  
  // --- Function to Hide Status ---
  hideStatus = () => {
    if (!this.commentStatusElement) return;
    this.commentStatusElement.style.display = 'none';
    this.commentStatusElement.textContent = '';
    if (this.commentListElement) {
      this.commentListElement.style.visibility = ''; // Show list again
    }
  }
  
  // --- Function to Fetch Comments ---
  fetchComments = async () => {
    if (!this.currentNewsId) {
      this.showStatus("Ni mogoče naložiti komentarjev - ni izbrana nobena novica.", true);
      return;
    }

    // If reached end return
    if ( 
      this.commentCount && 
      ( 
        (this.commentCount / this.commentPerPage) <= this.commentPage
      )
    ) return;

    //console.log(this.commentCount, this.commentPerPage, this.commentPage); 
    
    this.showStatus('Nalagam komentarje...');
    const listUrl = `${this.API_BASE}/comments/${this.currentNewsId}/list?client_id=${this.clientId}&sort=rating&order=desc&pageNumber=${this.commentPage}&pageSize=${this.commentPerPage}`; // Use consistent page size
    
    try {
      const response = await fetch(listUrl, {
        credentials: 'include', // Send cookies if required by API auth
        headers: {
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        let errorDetail = `HTTP napaka! Status: ${response.status}`;
        try {
          const errorData = await response.json();
          errorDetail = errorData.message || errorData.error || errorDetail;
        } catch (e) { /* Ignore if response body is not JSON */ }
        throw new Error(errorDetail);
      }
      
      const data = await response.json();
      
      // ** Adjust based on actual API response structure **
      this.commentCount = Number(data.response.count);
      this.commentPage++;
      const comments = data.response.comments || (Array.isArray(data) ? data : []);
      console.log('Fetched comments:', comments);
      
      this.hideStatus();
      this.renderComments(comments);
      
    } catch (error) {
      console.error(`Error fetching comments for newsId ${this.currentNewsId}:`, error);
      this.showStatus(`Napaka pri nalaganju komentarjev: ${error.message}`, true);
      this.renderComments([]); // Render empty state on fetch error
    }
  }

  parseBBCode(bbcode) {
    if (typeof bbcode !== 'string') return '';
  
    // Helper to ensure URL has protocol
    function formatUrl(url) {
      return /^(https?:)?\/\//i.test(url) ? url : 'https://' + url;
    }
  
    return bbcode
      // Bold
      .replace(/\[b\](.*?)\[\/b\]/gi, '<strong>$1</strong>')
      // Italic
      .replace(/\[i\](.*?)\[\/i\]/gi, '<em>$1</em>')
      // Underline
      .replace(/\[u\](.*?)\[\/u\]/gi, '<u>$1</u>')
      // URL with text
      .replace(/\[url=([^\]]+)\](.*?)\[\/url\]/gi, (_, url, text) => {
        const safeUrl = formatUrl(url);
        return `<a href="${safeUrl}" target="_blank">${text}</a>`;
      })
      // URL without text
      .replace(/\[url\]([^\]]+?)\[\/url\]/gi, (_, url) => {
        const safeUrl = formatUrl(url);
        return `<a href="${safeUrl}" target="_blank">${safeUrl}</a>`;
      });
  }
  
  
  // --- Function to Render Comments ---
  renderComments = (comments) => {
    if (!this.commentListElement) return;
    
    if (this.commentPage == 0) this.commentListElement.innerHTML = ''; // Clear previous comments
    
    if (!comments || comments.length === 0) {
      this.commentListElement.innerHTML = '<div class="list-group-item text-muted text-center p-3">Objava še nima komentarjev. Bodite prvi!</div>';
      return;
    }
    
    var self = this;
    
    comments.forEach(comment => {
      const commentDiv = document.createElement('div');
      commentDiv.className = 'list-group-item';
      
      // ** Adjust these based on your actual comment object structure **
      const comment_id = comment.id;
      const author_id = comment.user?.id;
      const author = comment.user?.uname || 'Anonymous';
      const dateStr = comment.stamp;
      const text = comment.content || '';
      const rating = comment.rating.up;
      const hasVoted = ( comment.vote  && comment.vote > 0 ) ? true : false;      
      // ** End adjustments **

      // Set comment ID for delete reference
      commentDiv.dataset.commentId = comment_id;
      
      let formattedDate = 'Neznan datum';
      if (dateStr) {
        try {
          // Attempt to format date nicely
          const dateObj = new Date(dateStr);
          // Example formatting (adjust as needed)
          formattedDate = dateObj.toLocaleString('sl-SL', {
            year: 'numeric', month: 'numeric', day: 'numeric',
            hour: 'numeric', minute: 'numeric', hour12: false
          });
          // Fallback if date is invalid
          if (isNaN(dateObj.getTime())) {
            formattedDate = dateStr; // Show original string if parsing failed
          }
        } catch (e) {
          formattedDate = dateStr; // Fallback for any error
        }
      }

      //console.log(self.parseBBCode(self.escapeHtml(text)));
      
      
      commentDiv.innerHTML = `<div>
        <img class="comment-author-img" src="//img.rtvslo.si/_up/avatars/${author_id}.png">
        <span class="comment-author">${self.escapeHtml(author)}</span>
        <span class="comment-date">${self.escapeHtml(formattedDate)}</span>
        ${self.isModerator ? '<button onclick="deleteComment(' + comment_id + ')" class="btn btn-sm btn-danger px-1 py-0 float-right ml-2" aria-label="Izbriši komentar"><span class="icon-trash" aria-hidden="true"></span></button>' : ''}
        <button class="comment-vote ${hasVoted ? 'voted' : '' }" aria-label="Glasuj za komentar" data-voted="${hasVoted ? 'true' : 'false' }" data-comment-id="${comment_id}" onclick="rateComment(this);">${rating}</button>
      </div>
      <p class="comment-text mb-0">${self.parseBBCode(self.escapeHtml(text))}</p>
      <div class="simple-dropdown d-block">
        <a href="javascript:void(0)" onclick="openSimpleDropdown(this);" style="font-size: 11px; color: rgba(0,0,0,.3);" class="report-comment btn-dropdown-toggle"><span class="icon-warning-outline mr-2"></span>Prijavi</a>
        <div class="dropdown-container" style="position: static;">
          <form class="report-form" data-comment-id="${comment_id}">
            <div class="form-group text-center">
              <label>Razlog prijave</label>
              <select class="form-control form-control-sm report-reason" required>
                <option disabled selected value="">Izberite razlog</option>
                <option value="1">Sovražni govor</option>
                <option value="2">Neprimerno</option>
                <option value="3">Izven tematike</option>
                <option value="4">Drugo</option>
              </select>
            </div>

            <div class="form-group mt-3">
              <textarea class="form-control form-control-sm report-text" rows="3" placeholder="Dodatno pojasnilo (neobvezno)"></textarea>
            </div>

            <div class="text-center mt-3">
              <button type="button" class="btn btn-sm btn-primary" onclick="submitReport(this)">Pošlji</button>
            </div>
          </form>
        </div>
      </div>`;
      self.commentListElement.appendChild(commentDiv);
    });
  }
  
  // --- Function to Handle Comment Submission ---
  handleCommentSubmit = async (event) => {
    event.preventDefault(); // Prevent default form submission
    
    if (!this.currentNewsId) {
      this.showStatus("Napaka: Ne morem objaviti komentarja, ni izbrana nobena novica.", true);
      return;
    }
    
    const commentText = this.commentInputElement.value.trim();
    if (!commentText) {
      this.commentInputElement.classList.add('is-invalid'); // Show validation feedback
      //this.commentInputElement.focus();
      return;
    }
    this.commentInputElement.classList.remove('is-invalid'); // Clear validation if text exists
    
    var self = this;
    
    const submitButton = this.commentFormElement.querySelector('button[type="submit"]');
    const originalButtonText = submitButton.innerHTML;
    submitButton.disabled = true;
    submitButton.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span> Pošiljam...';
    this.hideStatus(); // Clear previous status messages before sending
    
    const postUrl = `${this.API_BASE}/news/postComment/${this.currentNewsId}?client_id=${this.clientId}`;
    
    try {
      const response = await fetch(postUrl, {
        method: 'POST',
        credentials: 'include', // Send cookies etc.
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
          // Add other headers if required (e.g., CSRF token, Authorization)
        },
        // ** Verify API expects JSON like this. Adjust if it needs form data, etc. **
        body: JSON.stringify({ content: commentText, self_id:0 })
      });
      
      if (response.error) {
        let errorData = null;
        let errorMessage = `Napaka pri pošiljanju komentarja. Status: ${response.status}`;
        try {
          // Try to parse error response from API
          errorData = await response.json();
          errorMessage = errorData?.message || errorData?.error || errorMessage;
        } catch (e) { /* Ignore if body is not JSON */ }
        throw new Error(errorMessage);
      }
      
      // Success!
      this.commentInputElement.value = ''; // Clear input
      this.showStatus('Komentar uspešno poslan!', false); // Show success feedback
      // Fetch comments again to show the new one immediately
      this.fetchComments(); // Refresh the list
      
      // Hide success message after a short delay
      setTimeout(() => {
        // Check if status message is still the success one before hiding
        if (self.commentStatusElement && self.commentStatusElement.textContent === 'Komentar uspešno poslan!') {
          self.hideStatus();
        }
      }, 2000);
      
      
    } catch (error) {
      console.error(`Error posting comment for newsId ${this.currentNewsId}:`, error);
      // Display error near the form
      this.showStatus(`${error.message}`, true);
      // Don't auto-hide submission errors easily
      // Maybe add a dismiss button for the error later if needed
      
    } finally {
      // Re-enable button
      submitButton.disabled = false;
      submitButton.innerHTML = originalButtonText;
    }
  }
  
  // --- Utility to escape HTML ---
  escapeHtml = (unsafe) => {
    if (typeof unsafe !== 'string') return '';
    return unsafe
    .replace(/&/g, "&")
    .replace(/</g, "<")
    .replace(/>/g, ">")
    .replace(/"/g, "\"")
    .replace(/'/g, "'");
  }
}